/* The wishlist page.
 *
 * Swym knows WHAT is saved; the theme knows how a product should look. So this
 * asks Swym for the list, then asks the storefront to render each item with the
 * theme's own product card (templates/product.wishlist-card.liquid) and drops
 * the markup in. Nothing about a card is rebuilt here — that is the whole point.
 * Swatches, badges, price and the availability line stay identical to a
 * collection page, and stay correct when that snippet changes.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-wl-root]');
  if (!root || root.__willowWishlist) return;
  root.__willowWishlist = true;

  var grid = root.querySelector('[data-wl-grid]');
  var skeleton = root.querySelector('[data-wl-skeleton]');
  var empty = root.querySelector('[data-wl-empty]');
  var countEl = document.querySelector('[data-wl-count]');
  var actionsEl = document.querySelector('[data-wl-actions]');
  var sharedEl = document.querySelector('[data-wl-shared]');
  var statusEl = document.querySelector('[data-wl-status]');
  var sharePanel = document.querySelector('[data-wl-share-panel]');
  var shareField = document.querySelector('[data-wl-share-url]');

  /* Everything currently on the page, in Swym's own shape. Share, email and
   * clear all read from here rather than asking Swym again — the list is
   * already known, and a second fetch could disagree with what is on screen. */
  var saved = [];
  var renderVersion = 0;
  var swymApi = null;

  /* A shared list arrives as ?list=handle,handle,... — the page renders those
   * products instead of the viewer's own and drops every action that would
   * change a list, because the list being shown is not theirs. Handles rather
   * than a Swym share key: this is ours end to end, so a link keeps working
   * whatever the wishlist app does next. */
  var sharedHandles = (function () {
    var raw = new URLSearchParams(location.search).get('list');
    if (!raw) return null;
    var out = raw.split(',').map(function (h) { return h.trim(); }).filter(Boolean);
    return out.length ? out : null;
  })();

  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }

  function say(message) {
    if (!statusEl) return;
    statusEl.textContent = message || '';
    statusEl.hidden = !message;
  }

  function settle(state) {
    hide(skeleton);
    if (state === 'empty') {
      hide(grid);
      show(empty);
    } else {
      hide(empty);
      show(grid);
    }
  }

  function setCount(n) {
    // A previous link describes the old list; regenerate it on the next Share.
    hide(sharePanel);
    if(!sharedHandles) document.dispatchEvent(new CustomEvent('willow:wishlist-count',{detail:{count:n}}));
    if (countEl) {
      countEl.textContent = n === 1 ? '1 item' : n + ' items';
      countEl.hidden = n === 0;
    }
    // The actions act on the list, so they appear only when there is one, and
    // never on someone else's shared list.
    if (actionsEl) actionsEl.hidden = !(n > 0) || !!sharedHandles;
  }

  /* Swym stores a full product URL per item. The handle is what the storefront
   * needs, and taking it from the path is more reliable than trusting a
   * separate field that has changed shape between Swym versions. */
  function handleFrom(item) {
    return window.WillowWishlistData.handle(item);

  }

  function cardUrl(handle, variantId) {
    var url = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    url += 'products/' + encodeURIComponent(handle) + '?view=wishlist-card';
    // Ask for the variant that was actually saved, so the card shows the finish
    // the shopper chose rather than the product's default.
    if (variantId) url += '&variant=' + encodeURIComponent(variantId);
    return url;
  }

  function removeButton(item) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'wl-remove';
    button.setAttribute('aria-label', 'Remove from wishlist');
    button.innerHTML =
      '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6" ' +
      'stroke-linecap="round" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8"/></svg>';
    button.addEventListener('click', function () {
      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      var finished = false;
      var done = function () {
        if(finished) return;
        finished=true;
        saved=saved.filter(function(entry){return window.WillowWishlistData.key(entry)!==window.WillowWishlistData.key(item);});
        var wrapper = button.closest('.wl-item');
        if (wrapper) wrapper.remove();
        var left = grid.querySelectorAll('.wl-item').length;
        setCount(left);
        if (!left) settle('empty');
      };
      var failed = function () { button.removeAttribute('aria-busy'); };
      try {
        var result = window._swat.removeFromWishList(item, done, failed);
        if (result && typeof result.then === 'function') result.then(done, failed);
      }
      catch (error) { failed(); }
    });
    return button;
  }

  /* Last resort. Swym carries a title, an image, a price and a URL for every
   * saved item, so a wishlist can always be shown even when the theme card
   * cannot be fetched. Without this, one failing request per item is
   * indistinguishable from an empty wishlist — the page would tell a shopper
   * that nothing is saved while Swym is holding four vanities.
   */
  function fallbackCard(item) {
    var card = document.createElement('div');
    card.className = 'wl-fallback';
    var link = document.createElement('a');
    link.href = item.du || '#';
    if (item.iu) {
      var img = document.createElement('img');
      img.src = item.iu;
      img.alt = item.dt || '';
      img.loading = 'lazy';
      link.appendChild(img);
    }
    var title = document.createElement('span');
    title.className = 'wl-fallback__title';
    title.textContent = item.dt || 'Saved item';
    link.appendChild(title);
    card.appendChild(link);
    return card;
  }

  function renderItem(item) {
    var handle = handleFrom(item);
    var wrap = function (node) {
      var wrapper = document.createElement('div');
      wrapper.className = 'wl-item';
      wrapper.appendChild(node);
      // Nobody removes anything from a list that is not theirs.
      if (!sharedHandles) wrapper.appendChild(removeButton(item));
      return wrapper;
    };
    var fallback = function () {
      return wrap(fallbackCard(item));
    };
    if (!handle) return Promise.resolve(fallback());

    return fetch(cardUrl(handle, window.WillowWishlistData.variant(item)), { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.text() : null; })
      .then(function (html) {
        if (!html || !html.trim()) return fallback();
        var wrapper = document.createElement('div');
        wrapper.className = 'wl-item';
        wrapper.innerHTML = html;
        if (wrapper.querySelector('[data-wl-excluded]')) return null;
        // An alternate template that is missing answers with the whole page
        // rather than a card. Anything carrying a header is not a card.
        if (!wrapper.querySelector('.product-card') || wrapper.querySelector('header, #shopify-section-header')) return fallback();
        if (!sharedHandles) wrapper.appendChild(removeButton(item));
        return wrapper;
      })
      .catch(fallback);
  }

  function render(items) {
    // Preserve distinct saved variants and collapse only identical entries.
    var unique = window.WillowWishlistData.items(items);
    var version = ++renderVersion;

    saved = unique;

    if (!unique.length) {
      grid.textContent = '';
      setCount(0);
      settle('empty');
      return;
    }

    Promise.all(unique.map(renderItem)).then(function (nodes) {
      if(version !== renderVersion) return;
      grid.textContent = '';
      saved = unique.filter(function(item, index) { return !!nodes[index]; });
      var live = nodes.filter(Boolean);
      // Keep the saved-item fallback when a product card is unavailable.
      live.forEach(function (node) { grid.appendChild(node); });
      setCount(live.length);
      settle(live.length ? 'list' : 'empty');
      // These cards arrive long after willow-collection-actions.js has run, so
      // nothing has moved their Compare button into the corner of the photo or
      // restored its selected state. That script re-syncs on this event, which
      // is what the collection grid fires after a filter reload.
      document.dispatchEvent(new CustomEvent('collection:reloaded'));
    });
  }

  /* What swat.fetch hands back has two shapes in the wild: an array of LISTS,
   * each carrying its saved products in `listcontents`, or — as on this store —
   * a flat array of the saved products themselves. Reading only the first shape
   * is what made this page insist the wishlist was empty however much was
   * saved to it; the PDP heart, which reads the flat shape, knew better.
   * Accept both, and let anything else fall through as empty. */
  function itemsFrom(payload) {
    var items = [];
    (Array.isArray(payload) ? payload : []).forEach(function (entry) {
      if (!entry) return;
      if (Array.isArray(entry.listcontents)) items = items.concat(entry.listcontents);
      else if (entry.empi || entry.du) items.push(entry);
    });
    return items;
  }

  /* ---- Share, email, clear all ------------------------------------------
   *
   * Only clear all talks to Swym, and only through removeFromWishList, which
   * is the call the product page already relies on. Share and email are built
   * from what is on the page: a link the shop owns, and a mailto. Nothing here
   * depends on a Swym method whose name might change under us — the last bug
   * on this page was exactly that.
   */

  function shareUrl() {
    var handles = saved.map(function(item){var handle=handleFrom(item);var variant=window.WillowWishlistData.variant(item);return handle+(variant?'~'+variant:'');}).filter(Boolean);
    // A URL is not a database. Past about forty handles the link gets long
    // enough that mail clients and chat apps start breaking it across lines,
    // so it carries the first forty and says so.
    var capped = handles.slice(0, 40);
    return {
      url: location.origin + location.pathname + '?list=' + capped.join(','),
      shown: capped.length,
      total: handles.length
    };
  }

  function mailtoUrl() {
    var link = shareUrl();
    var lines = saved.slice(0, 25).map(function (item) {
      var title = item.dt || 'Vanity';
      var url = item.du || (handleFrom(item) ? location.origin + '/products/' + handleFrom(item) : '');
      return url ? title + '\n' + url : title;
    });
    if (saved.length > lines.length) {
      lines.push('...and ' + (saved.length - lines.length) + ' more.');
    }
    var body = 'My wishlist from Willow Bath and Vanity:\n\n'
      + lines.join('\n\n')
      + '\n\nThe whole list: ' + link.url + '\n';
    return 'mailto:?subject=' + encodeURIComponent('My Willow Bath and Vanity wishlist')
      + '&body=' + encodeURIComponent(body);
  }

  function legacyCopy(text) {
    return new Promise(function (resolve, reject) {
      var field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      var previous = document.activeElement;
      document.body.appendChild(field);
      field.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (error) { ok = false; }
      document.body.removeChild(field);
      if (previous && previous.focus) previous.focus({preventScroll: true});
      ok ? resolve() : reject(new Error('copy failed'));
    });
  }

  function copy(text) {
    return Promise.resolve().then(function () {
      if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('Clipboard unavailable');
      return navigator.clipboard.writeText(text);
    }).catch(function () { return legacyCopy(text); });
  }

  function shareMessage(name, link) {
    var text = sharePanel ? sharePanel.dataset[name] || '' : '';
    return text.replace('__shown__', link.shown).replace('__total__', link.total);
  }

  function revealShareLink(link, select) {
    if (!sharePanel || !shareField) return;
    shareField.value = link.url;
    var limit = sharePanel.querySelector('[data-wl-share-limit]');
    if (limit) {
      limit.textContent = link.total > link.shown ? shareMessage('limited', link) : '';
      limit.hidden = !limit.textContent;
    }
    show(sharePanel);
    if (select) {
      shareField.focus({preventScroll: true});
      shareField.select();
    }
  }

  function copyShareLink(link) {
    revealShareLink(link, false);
    return copy(link.url).then(function () {
      say(shareMessage('copied', link));
    }, function () {
      revealShareLink(link, true);
      say(shareMessage('failed', link));
    });
  }

  function bindShare() {
    var button = document.querySelector('[data-wl-share]');
    if (!button) return;
    button.addEventListener('click', function () {
      var link = shareUrl();
      revealShareLink(link, false);
      say('');
      if (navigator.share) {
        try {
          navigator.share({ title: 'My Willow wishlist', url: link.url }).then(function () {
            say('');
          }).catch(function (error) {
            // Cancelling a share sheet is intentional; the manual link remains.
            if (!error || error.name !== 'AbortError') copyShareLink(link);
          });
        } catch (error) { copyShareLink(link); }
        return;
      }
      copyShareLink(link);
    });
    var copyButton = document.querySelector('[data-wl-copy-link]');
    if (copyButton) copyButton.addEventListener('click', function () { copyShareLink(shareUrl()); });
    if (shareField) shareField.addEventListener('click', function () { shareField.select(); });
  }

  function bindEmail() {
    var link = document.querySelector('[data-wl-email]');
    if (!link) return;
    link.addEventListener('click', function () {
      link.href = mailtoUrl();
      say(saved.length > 25 ? 'Opening your mail app with the first 25, and a link to all of them.' : '');
    });
  }

  /* Removing one at a time rather than all at once: sixty-odd parallel calls
   * to a third-party API is how you get rate-limited half way through and end
   * up with a list that is neither cleared nor intact. */
  function clearAll(button) {
    var queue = saved.slice();
    var total = queue.length;
    var failures = 0;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    function finish() {
      button.removeAttribute('aria-busy');
      button.disabled = false;
      if (failures) {
        say(failures + ' of ' + total + ' could not be removed. Reload and try again.');
        return;
      }
      say('');
      grid.innerHTML = '';
      saved = [];
      setCount(0);
      settle('empty');
    }

    function step(i) {
      if (i >= queue.length) return finish();
      say('Removing ' + (i + 1) + ' of ' + total + '...');
      var next = function () { step(i + 1); };
      var failed = function () { failures++; next(); };
      try {
        var result = swymApi.removeFromWishList(queue[i], next, failed);
        if (result && typeof result.then === 'function') result.then(next, failed);
      }
      catch (error) { failed(); }
    }

    step(0);
  }

  /* Two taps rather than a confirm dialog: the first turns the button into the
   * question, the second answers it. Clearing a wishlist cannot be undone, so
   * it must not be one stray tap away. */
  function bindClear() {
    var button = document.querySelector('[data-wl-clear]');
    if (!button) return;
    var resting = button.textContent;
    var armed = false;
    var timer = null;

    function disarm() {
      armed = false;
      button.textContent = resting;
      button.classList.remove('is-armed');
      window.clearTimeout(timer);
    }

    button.addEventListener('click', function () {
      if (!saved.length || !swymApi) return;
      if (!armed) {
        armed = true;
        button.textContent = 'Remove all ' + saved.length + '?';
        button.classList.add('is-armed');
        timer = window.setTimeout(disarm, 6000);
        return;
      }
      disarm();
      clearAll(button);
    });

    document.addEventListener('click', function (event) {
      if (armed && event.target !== button) disarm();
    });
  }

  function init(swat) {
    if (!swat || typeof swat.fetch !== 'function') {
      settle('empty');
      return;
    }
    if(swymApi===swat) return;
    swymApi = swat;
    swat.fetch(
      function (payload) { render(itemsFrom(payload)); },
      function () { settle('empty'); }
    );
  }

  /* A shared list needs no Swym at all: the handles are in the URL, and the
   * cards come from the storefront. It renders even for a visitor who has
   * never used the wishlist. */
  function initShared() {
    show(sharedEl);
    render(sharedHandles.map(function (handle) {
      var parts=handle.split('~');return {du:'/products/'+parts[0],epi:parts[1]||''};
    }));
  }

  bindShare();
  bindEmail();
  bindClear();

  if (sharedHandles) {
    initShared();
    return;
  }

  // If Swym never arrives, the page must not sit on a skeleton for ever: after
  // ten seconds show the empty state, which at least offers a way onward.
  var settled = false;
  var guard = window.setTimeout(function () {
    if (!settled) settle('empty');
  }, 10000);

  var wrapped = function (swat) {
    settled = true;
    window.clearTimeout(guard);
    init(swat);
  };

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(wrapped);
  if (window._swat) wrapped(window._swat);
})();


(function(){
  document.addEventListener('toggle',function(event){
    var details=event.target;
    if(!details.matches || !details.matches('[data-wl-alternatives]') || !details.open || details.dataset.loaded)return;
    details.dataset.loaded='loading';
    var box=details.querySelector('[data-wl-alternatives-results]');box.textContent='Finding available alternatives…';
    var url=new URL((window.Shopify?.routes?.root || '/')+'recommendations/products.json',location.origin);
    url.searchParams.set('product_id',details.dataset.productId);url.searchParams.set('limit','10');url.searchParams.set('intent','related');
    fetch(url).then(function(response){if(!response.ok)throw new Error('Unavailable');return response.json();}).then(function(data){
      var products=(data.products||[]).filter(function(product){return product.available && String(product.id)!==details.dataset.productId && !product.tags?.includes('dont_show');});
      var width=details.dataset.width;
      products.sort(function(a,b){return Number(b.title.includes(width+' in.'))-Number(a.title.includes(width+' in.'));});
      box.textContent='';
      products.slice(0,3).forEach(function(product){
        var link=document.createElement('a');link.className='wl-alternative';link.href=product.url;
        if(product.featured_image){var img=document.createElement('img');img.src=product.featured_image;img.alt='';img.loading='lazy';link.appendChild(img);}
        var text=document.createElement('span');text.textContent=product.title;link.appendChild(text);box.appendChild(link);
      });
      if(!products.length)box.textContent='See all available vanities to find another finish or size.';
      details.dataset.loaded='true';
    }).catch(function(){box.textContent='Browse current availability below.';delete details.dataset.loaded;});
  },true);
})();

